import { formatAmountWithUnit, GIFT_CODE_TYPE_LABELS } from "@loot-os/api-contract";
import { Badge } from "@loot-os/ui/components/badge";
import { Button } from "@loot-os/ui/components/button";
import { Card, CardContent } from "@loot-os/ui/components/card";
import { DataTable } from "@loot-os/ui/components/data-table";
import { toast } from "@loot-os/ui/components/sonner";
import { createDataTableColumns } from "@loot-os/ui/lib/dataTable";
import { formatNumber } from "@loot-os/ui/lib/format";
import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "@tanstack/react-router";
import { Copy } from "lucide-react";
import { useMemo, useState } from "react";

import { ErrorAlert } from "../components/ErrorAlert";
import { Stat } from "../components/Facts";
import { ShortDate } from "../components/ShortDate";
import { VoidGiftCodeBatchButton, VoidGiftCodeButton } from "../components/VoidGiftCodeButtons";
import { useCurrency } from "../lib/currency";
import {
  GIFT_CODE_STATE_BADGE,
  type GiftCodeRow,
  giftCodeBatchState,
  giftCodesCsvName,
} from "../lib/giftCodes";
import { useTRPC } from "../lib/trpc";

const codeColumns = createDataTableColumns<GiftCodeRow>();
const CODE_PINNED = { start: [], end: ["actions"] };

async function copyCode(code: string) {
  await navigator.clipboard.writeText(code);
  toast.success(`Copied ${code}.`);
}

function buildCodeColumns(tenantId: string) {
  return codeColumns.columns([
    codeColumns.accessor("code", {
      header: "Code",
      size: 240,
      cell: ({ getValue }) => (
        <span className="flex items-center gap-1">
          <span className="font-mono text-xs">{getValue()}</span>
          <Button
            type="button"
            variant="ghost"
            size="icon-xs"
            aria-label={`Copy ${getValue()}`}
            onClick={() => void copyCode(getValue())}
          >
            <Copy />
          </Button>
        </span>
      ),
    }),
    codeColumns.accessor("status", {
      header: "Status",
      cell: ({ getValue }) => (
        <Badge variant={GIFT_CODE_STATE_BADGE[getValue()]}>{getValue()}</Badge>
      ),
      meta: { filter: "text" },
    }),
    codeColumns.accessor((code) => code.redeemedBy?.externalId ?? "", {
      id: "redeemedBy",
      header: "Redeemed by",
      cell: ({ row }) => {
        const shopper = row.original.redeemedBy;
        if (!shopper) return <span className="text-muted-foreground">—</span>;
        return (
          <Link
            to="/tenants/$tenantId/users/$userId"
            params={{ tenantId, userId: shopper.endUserId }}
            className="font-mono text-xs hover:underline"
          >
            {shopper.externalId}
            {shopper.username ? ` (@${shopper.username})` : ""}
          </Link>
        );
      },
      meta: { filter: "text" },
    }),
    codeColumns.accessor("redeemedAt", {
      header: "Redeemed",
      cell: ({ getValue }) => {
        const redeemedAt = getValue();
        return redeemedAt ? <ShortDate value={redeemedAt} /> : "—";
      },
      meta: { csv: (value) => (typeof value === "string" ? value : "") },
    }),
    codeColumns.display({
      id: "actions",
      size: 88,
      cell: ({ row }) =>
        row.original.status === "active" || row.original.status === "expired" ? (
          <VoidGiftCodeButton codeId={row.original.id} code={row.original.code} />
        ) : null,
    }),
  ]);
}

/**
 * One batch of gift codes: what each is worth, how many were redeemed, and every code, which the
 * table downloads as a CSV. Voiding the batch or a code stops it from redeeming.
 */
export function GiftCodeBatchPage() {
  const { tenantId, batchId } = useParams({
    from: "/app/tenants/$tenantId/gift-codes/$batchId",
  });
  const trpc = useTRPC();
  const currency = useCurrency();
  const [now] = useState(() => new Date());
  const detail = useQuery(trpc.giftCodes.batch.queryOptions({ batchId }));
  const columns = useMemo(() => buildCodeColumns(tenantId), [tenantId]);

  if (detail.error) return <ErrorAlert message={detail.error.message} />;
  if (!detail.data) return <p className="text-sm text-muted-foreground">Loading…</p>;

  const { batch, codes } = detail.data;
  const state = giftCodeBatchState(batch, now);
  const active = codes.filter((code) => code.status === "active").length;
  const unredeemed = batch.codeCount - batch.redeemedCount;

  return (
    <div className="flex flex-col gap-6">
      <header className="flex flex-wrap items-start justify-between gap-x-6 gap-y-3">
        <div className="flex min-w-0 flex-col gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-2xl font-semibold tracking-tight">
              {GIFT_CODE_TYPE_LABELS[batch.type]} codes
            </h1>
            <Badge variant={GIFT_CODE_STATE_BADGE[state]}>{state}</Badge>
          </div>
          <p className="text-sm text-muted-foreground">
            {formatAmountWithUnit(batch.amountCents, currency)} each · created{" "}
            <ShortDate value={batch.createdAt} /> ·{" "}
            {batch.expiresAt ? (
              <>
                expires <ShortDate value={batch.expiresAt} />
              </>
            ) : (
              "never expires"
            )}
          </p>
          {batch.note && <p className="max-w-prose text-sm">{batch.note}</p>}
        </div>
        {state !== "voided" && unredeemed > 0 && (
          <VoidGiftCodeBatchButton batchId={batch.id} unredeemed={unredeemed} />
        )}
      </header>

      {/* Hairlines between the cells come from the border-coloured gap showing through. */}
      <Card className="grid grid-cols-2 gap-px bg-border py-0 sm:grid-cols-4">
        <div className="bg-card px-5 py-4">
          <Stat label="Codes">{formatNumber(batch.codeCount)}</Stat>
        </div>
        <div className="bg-card px-5 py-4">
          <Stat label="Redeemed">{formatNumber(batch.redeemedCount)}</Stat>
        </div>
        <div className="bg-card px-5 py-4">
          <Stat label="Credited">
            {formatAmountWithUnit(batch.redeemedCount * batch.amountCents, currency)}
          </Stat>
        </div>
        <div className="bg-card px-5 py-4">
          <Stat label="Still redeemable">
            {formatAmountWithUnit(active * batch.amountCents, currency)}
          </Stat>
        </div>
      </Card>

      <Card>
        <CardContent>
          <DataTable
            columns={columns}
            data={codes}
            getRowId={(code) => code.id}
            pinned={CODE_PINNED}
            search="Search codes"
            csvName={giftCodesCsvName(batch)}
            emptyMessage="This batch has no codes."
          />
        </CardContent>
      </Card>
    </div>
  );
}
